/**
 * Sync Health Dashboard — Phase 4
 *
 * Per-connector observability: status, last synced, error rate,
 * pending event counts, and each connector's Field Sync Rules
 * (FieldSyncRulesEditor).
 *
 * Also includes an AI-powered trust-rule advisor that looks at
 * recent conflict history and suggests changes to which system is the source.
 *
 * Route: /prioritization/settings/sync-health
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowLeftRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Edit3,
  Loader2,
  RefreshCcw,
  ShieldCheck,
  Wifi,
  WifiOff,
  Zap,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { triggerDispatch } from '../../../lib/syncEngine';
import { triggerApply } from '../../../lib/connectors';
import { FieldSyncRulesEditor } from './FieldSyncRulesEditor';
import type { StoredFieldRules } from '../../../../supabase/functions/_shared/fieldSyncRules';

// ── Types ─────────────────────────────────────────────────────

type SyncDirection = 'inbound' | 'outbound' | 'both';

interface ConnectorConfig {
  id: string;
  connectorType: string;
  displayName: string;
  trustRules: StoredFieldRules;
  fieldMappings: unknown;
  syncDirection: SyncDirection;
  isActive: boolean;
  lastSyncedAt: string | null;
  syncError: string | null;
}

interface ConnectorStats {
  pending: number;
  synced: number;
  conflicted: number;
  failed: number;
  lastEventAt: string | null;
}

// ── Constants ─────────────────────────────────────────────────

const CONNECTOR_TYPE_ICONS: Record<string, string> = {
  jira: 'J',
  linear: 'L',
  github: 'G',
  miro: 'M',
  slack: 'S',
  notion: 'N',
  asana: 'A',
};

const DIRECTION_LABELS: Record<SyncDirection, string> = {
  inbound: 'Inbound only',
  outbound: 'Outbound only',
  both: 'Bidirectional',
};

function formatTimestamp(iso: string | null) {
  if (!iso) return 'Never';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return iso; }
}

// ── ConnectorCard ─────────────────────────────────────────────

interface ConnectorCardProps {
  connector: ConnectorConfig;
  stats: ConnectorStats | null;
  onTriggerDispatch: (id: string) => void;
  onTriggerApply: (id: string) => void;
  onRulesUpdated: (id: string, newRules: StoredFieldRules) => void;
}

function ConnectorCard({
  connector,
  stats,
  onTriggerDispatch,
  onTriggerApply,
  onRulesUpdated,
}: ConnectorCardProps) {
  const [showRulesEditor, setShowRulesEditor] = useState(false);
  const [isActioning, setIsActioning] = useState(false);

  const initial = CONNECTOR_TYPE_ICONS[connector.connectorType] ?? connector.connectorType.slice(0, 1).toUpperCase();
  const isHealthy = !connector.syncError && connector.isActive;
  const conflictCount = stats?.conflicted ?? 0;
  const failedCount = stats?.failed ?? 0;

  async function handleDispatch() {
    setIsActioning(true);
    await onTriggerDispatch(connector.id);
    setIsActioning(false);
  }

  async function handleApply() {
    setIsActioning(true);
    await onTriggerApply(connector.id);
    setIsActioning(false);
  }

  return (
    <div className={`border  bg-[var(--bg-raised)] dark:bg-[var(--surface)] overflow-hidden ${
      connector.syncError
        ? 'border-[var(--coral)] dark:border-[var(--coral)]'
        : 'border-[var(--rule)]'
    }`}>
      {/* Header row */}
      <div className="flex items-center gap-4 px-5 py-4">
        {/* Avatar */}
        <div className={`h-10 w-10  flex items-center justify-center text-sm font-bold flex-shrink-0 ${
          connector.isActive ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
        }`}>
          {initial}
        </div>

        {/* Name + direction */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {connector.displayName || connector.connectorType}
            </span>
            <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {DIRECTION_LABELS[connector.syncDirection]}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {isHealthy
              ? <Wifi className="h-3 w-3 text-[var(--green)]" />
              : <WifiOff className="h-3 w-3 text-[var(--coral)]" />}
            <span className={`text-xs ${isHealthy ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)] dark:text-[var(--coral)]'}`}>
              {isHealthy ? `Last synced ${formatTimestamp(connector.lastSyncedAt)}` : connector.syncError ?? 'Inactive'}
            </span>
          </div>
        </div>

        {/* Stats pills */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {stats && (
            <>
              {conflictCount > 0 && (
                <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 text-[var(--coral)] dark:text-[var(--coral)] rounded-full border border-[var(--coral)] dark:border-[var(--coral)]">
                  <AlertTriangle className="h-3 w-3" />
                  {conflictCount}
                </span>
              )}
              {failedCount > 0 && (
                <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 text-[var(--mustard)] dark:text-[var(--mustard)] rounded-full border border-[var(--mustard)] dark:border-[var(--mustard)]">
                  <Clock className="h-3 w-3" />
                  {failedCount} failed
                </span>
              )}
              {conflictCount === 0 && failedCount === 0 && (
                <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-[var(--green-dim)] dark:bg-[var(--green)]/20 text-[var(--green)] dark:text-[var(--text-ter)] rounded-full border border-[var(--green)] dark:border-[var(--green)]">
                  <CheckCircle2 className="h-3 w-3" />
                  Healthy
                </span>
              )}
            </>
          )}

          {/* Actions */}
          {connector.syncDirection !== 'inbound' && (
            <button
              onClick={handleDispatch}
              disabled={isActioning}
              title="Run outbound dispatch"
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors disabled:opacity-50"
            >
              {isActioning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            </button>
          )}
          {connector.syncDirection !== 'outbound' && (
            <button
              onClick={handleApply}
              disabled={isActioning}
              title="Run inbound apply"
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--mustard)] hover:bg-[var(--mustard-dim)] dark:hover:bg-[var(--mustard)]/20 transition-colors disabled:opacity-50"
            >
              {isActioning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowLeftRight className="h-3.5 w-3.5" />}
            </button>
          )}

          {/* Trust rules toggle */}
          <button
            onClick={() => setShowRulesEditor((v) => !v)}
            title="Field sync rules"
            className={`p-1.5  transition-colors ${
              showRulesEditor
                ? 'text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20'
                : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]'
            }`}
          >
            <Edit3 className="h-3.5 w-3.5" />
          </button>

          {/* Expand */}
          <button
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            onClick={() => setShowRulesEditor((v) => !v)}
          >
            {showRulesEditor ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Sync error message */}
      {connector.syncError && (
        <div className="px-5 pb-3">
          <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 px-3 py-2 flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            {connector.syncError}
          </p>
        </div>
      )}

      {/* Trust rules editor */}
      {showRulesEditor && (
        <div className="px-5 pb-5 pt-1 border-t border-[var(--rule)]">
          <FieldSyncRulesEditor
            connectorId={connector.id}
            connectorType={connector.connectorType}
            currentRules={connector.trustRules}
            fieldMappings={connector.fieldMappings}
            connectorDirection={connector.syncDirection}
            onSaved={(newRules) => {
              onRulesUpdated(connector.id, newRules);
              setShowRulesEditor(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export default function SyncHealth() {
  const [connectors, setConnectors] = useState<ConnectorConfig[]>([]);
  const [stats, setStats] = useState<Map<string, ConnectorStats>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<string | null>(null);

  const workspaceId = getCurrentWorkspaceId();

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    setError(null);
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) throw new Error('Supabase not available');

      const [cfgResult, eventsResult] = await Promise.all([
        supabase
          .from('connector_configs')
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: true }),
        supabase
          .from('change_events')
          .select('source, sync_status, created_at')
          .eq('workspace_id', workspaceId)
          .neq('source', 'priorities_ai')
          .order('created_at', { ascending: false })
          .limit(500),
      ]);

      if (cfgResult.error) throw new Error(cfgResult.error.message);

      const mapped: ConnectorConfig[] = (cfgResult.data ?? [])
        // Only show connectors that are active AND have real credentials.
        // Rows with is_active=true but empty auth_data are "available" (workspace-enabled
        // but not yet authenticated) — they are not connected and should not appear here.
        .filter((row) => {
          if (!row.is_active) return false;
          const authData = (row.auth_data as Record<string, unknown>) ?? {};
          return Object.keys(authData).length > 0;
        })
        .map((row) => ({
          id: row.id as string,
          connectorType: row.connector_type as string,
          displayName: (row.display_name as string) || (row.connector_type as string),
          trustRules: (row.trust_rules as StoredFieldRules) ?? {},
          fieldMappings: row.field_mappings,
          syncDirection: (row.sync_direction as SyncDirection) ?? 'both',
          isActive: row.is_active as boolean,
          lastSyncedAt: row.last_synced_at as string | null,
          syncError: row.sync_error as string | null,
        }));
      setConnectors(mapped);

      // Aggregate stats by connector type from change_events
      const statsMap = new Map<string, ConnectorStats>();
      for (const row of eventsResult.data ?? []) {
        const src = row.source as string;
        const existing = statsMap.get(src) ?? { pending: 0, synced: 0, conflicted: 0, failed: 0, lastEventAt: null };
        const status = row.sync_status as string;
        if (status === 'pending') existing.pending++;
        else if (status === 'synced') existing.synced++;
        else if (status === 'conflicted' || status === 'quarantined') existing.conflicted++;
        if (!existing.lastEventAt || row.created_at > existing.lastEventAt) {
          existing.lastEventAt = row.created_at as string;
        }
        statsMap.set(src, existing);
      }

      // Also check dispatch log for failures
      const { data: dispatchData } = await supabase
        .from('sync_dispatch_log')
        .select('connector_type, status')
        .eq('workspace_id', workspaceId)
        .eq('status', 'failed');

      for (const row of dispatchData ?? []) {
        const src = row.connector_type as string;
        const existing = statsMap.get(src) ?? { pending: 0, synced: 0, conflicted: 0, failed: 0, lastEventAt: null };
        existing.failed++;
        statsMap.set(src, existing);
      }

      setStats(statsMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connector health');
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  async function handleTriggerDispatch(connectorConfigId: string) {
    if (!workspaceId) return;
    setActionResult(null);
    const { data, error: dispErr } = await triggerDispatch({ workspaceId, mode: 'pending' });
    if (dispErr) setActionResult(`Dispatch error: ${dispErr}`);
    else if (data) setActionResult(`Dispatched ${data.dispatched} events, ${data.conflicts} conflicts`);
    load();
    void connectorConfigId;
  }

  async function handleTriggerApply(connectorConfigId: string) {
    if (!workspaceId) return;
    setActionResult(null);
    const result = await triggerApply(workspaceId, { mode: 'pending' });
    if (result.error) setActionResult(`Apply error: ${result.error}`);
    else setActionResult(`Applied ${result.applied} events, ${result.conflicts} conflicts, ${result.no_mapping} unmapped`);
    load();
    void connectorConfigId;
  }

  function handleRulesUpdated(connectorId: string, newRules: StoredFieldRules) {
    setConnectors((prev) =>
      prev.map((c) => (c.id === connectorId ? { ...c, trustRules: newRules } : c))
    );
  }

  const totalConflicts = Array.from(stats.values()).reduce((s, v) => s + v.conflicted, 0);
  const totalPending = Array.from(stats.values()).reduce((s, v) => s + v.pending, 0);
  const healthyConnectors = connectors.filter((c) => c.isActive && !c.syncError).length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Activity className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Sync Health"
        subtitle="Per-connector observability, trust-rule configuration, and sync pipeline controls."
        action={
          <button
            onClick={load}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] disabled:opacity-50 transition-colors"
          >
            {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
            Refresh
          </button>
        }
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 border border-[var(--green)] dark:border-[var(--green)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-4 w-4 text-[var(--green)]" />
            <span className="text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Healthy connectors</span>
          </div>
          <div className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            {healthyConnectors} / {connectors.length}
          </div>
        </div>
        <div className="p-4 border border-[var(--mustard)] dark:border-[var(--mustard)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="h-4 w-4 text-[var(--mustard)]" />
            <span className="text-xs font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">Pending events</span>
          </div>
          <div className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{totalPending}</div>
        </div>
        <div className="p-4 border border-[var(--coral)] dark:border-[var(--coral)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-[var(--coral)]" />
            <span className="text-xs font-medium text-[var(--coral)] dark:text-[var(--coral)]">Active conflicts</span>
          </div>
          <div className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{totalConflicts}</div>
        </div>
      </div>

      {/* Action result */}
      {actionResult && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 border border-[var(--rule)] text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
          {actionResult}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {isLoading && connectors.length === 0 && (
        <div className="flex items-center justify-center py-16 text-[var(--text-ter)]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading connectors…
        </div>
      )}

      {!isLoading && connectors.length === 0 && !error && (
        <div className="text-center py-16 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <Activity className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">No connectors configured</p>
          <p className="text-xs mt-1">
            Add connectors in the <a href="/prioritization/settings/connectors" className="underline">Connectors settings</a> page.
          </p>
        </div>
      )}

      {connectors.length > 0 && (
        <div className="space-y-3">
          {connectors.map((connector) => (
            <ConnectorCard
              key={connector.id}
              connector={connector}
              stats={stats.get(connector.connectorType) ?? null}
              onTriggerDispatch={handleTriggerDispatch}
              onTriggerApply={handleTriggerApply}
              onRulesUpdated={handleRulesUpdated}
            />
          ))}
        </div>
      )}
    </div>
  );
}
